import { Button } from "@/components/ui/Button";
import type { CitationRoute } from "@/features/analytics/types/events";
import { DocumentViewerPage } from "@/features/citations/pages/DocumentViewerPage";
import { PaperViewerPage } from "@/features/citations/pages/PaperViewerPage";
import { parseHighlights } from "@/features/citations/utils/parseHighlights";
import { ChevronDown, ChevronUp, ExternalLink, X } from "lucide-react";
import { useMemo } from "react";
import { useCitations } from "../context/CitationProvider";
import {
  citationStableKey,
  resolvePaperDisplay,
  type Citation,
} from "../utils/citations";

const buildViewerPath = (citation: Citation): string => {
  if (citation._citationUrl) return citation._citationUrl;
  const source = citation.source?.toLowerCase() === "sec" ? "sec" : "papers";
  const base = `/citations/${source}/${citation.doc_id}`;
  if (citation.line_number != null) return `${base}#L${citation.line_number}`;
  return base;
};

const getCitationRoute = (citation: Citation): CitationRoute => {
  const src = citation.source?.toLowerCase();
  if (src === "fda") return "fda";
  if (src === "trials" || src === "clinicaltrials") return "clinicaltrials";
  if (src === "patents") return "patents";
  if (src === "sec") return "sec";
  if (src === "clipboard") return "clipboard";
  return "papers";
};

const getHighlightsFromCitation = (citation: Citation): Set<number> => {
  if (citation._citationUrl) {
    const hashIdx = citation._citationUrl.indexOf("#");
    if (hashIdx >= 0) return parseHighlights(citation._citationUrl.slice(hashIdx));
  }
  if (citation.line_number != null) return new Set([citation.line_number]);
  return new Set();
};

const CitationViewerInline = ({ citation }: { citation: Citation }) => {
  const route = getCitationRoute(citation);
  const highlights = useMemo(() => getHighlightsFromCitation(citation), [citation]);

  if (route === "papers") {
    return (
      <div className="h-full overflow-y-auto [&>div]:!fixed-none [&>div]:!relative [&>div]:!inset-auto [&>div]:min-h-full">
        <PaperViewerPage docIdProp={citation.doc_id} highlightsProp={highlights} />
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto [&>div]:!fixed-none [&>div]:!relative [&>div]:!inset-auto [&>div]:min-h-full">
      <DocumentViewerPage source={route} docIdProp={citation.doc_id} highlightsProp={highlights} />
    </div>
  );
};

export const CitationSidebar = ({ onClose }: { onClose: () => void }) => {
  const { activeCitations, selectedCitation, openCitation } = useCitations();

  const selectedKey = selectedCitation
    ? citationStableKey(selectedCitation)
    : null;

  const selectedIndex = useMemo(
    () =>
      selectedKey
        ? activeCitations.findIndex((c) => citationStableKey(c) === selectedKey)
        : -1,
    [activeCitations, selectedKey],
  );

  if (!selectedCitation) return null;

  const paper = resolvePaperDisplay(
    selectedCitation.source,
    selectedCitation.doc_id,
    selectedCitation.doi,
    selectedCitation.public_url,
  );

  const headerTitle =
    selectedCitation.content ||
    selectedCitation.doc_title ||
    selectedCitation.section ||
    paper.label;

  const canGoPrev = selectedIndex > 0;
  const canGoNext =
    selectedIndex >= 0 && selectedIndex < activeCitations.length - 1;

  const viewerPath = buildViewerPath(selectedCitation);

  return (
    <div className="flex h-full w-full flex-col bg-white">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-200 px-4 py-2.5">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-3xs text-gray-400 font-mono flex-shrink-0">
            [{selectedCitation.id}]
          </span>
          <span className="text-xs text-gray-600 truncate">{headerTitle}</span>
        </div>
        <div className="flex items-center gap-0.5 flex-shrink-0">
          <Button
            variant="ghost"
            size="icon-xs"
            onClick={() =>
              canGoPrev &&
              openCitation(activeCitations[selectedIndex - 1], activeCitations)
            }
            disabled={!canGoPrev}
            aria-label="Previous citation"
            title="Previous citation"
            className="rounded text-gray-400 disabled:opacity-30"
          >
            <ChevronUp className="w-3.5 h-3.5" aria-hidden />
          </Button>
          <Button
            variant="ghost"
            size="icon-xs"
            onClick={() =>
              canGoNext &&
              openCitation(activeCitations[selectedIndex + 1], activeCitations)
            }
            disabled={!canGoNext}
            aria-label="Next citation"
            title="Next citation"
            className="rounded text-gray-400 disabled:opacity-30"
          >
            <ChevronDown className="w-3.5 h-3.5" aria-hidden />
          </Button>
          {paper.url && (
            <a
              href={paper.url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Open source in new tab"
              title="Open source in new tab"
              className="h-6 w-6 flex items-center justify-center rounded text-gray-400 hover:bg-gray-100 hover:text-blue-600"
            >
              <ExternalLink className="w-3.5 h-3.5" aria-hidden />
            </a>
          )}
          <a
            href={viewerPath}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Open citation in new tab"
            title="Open citation in new tab"
            className="h-6 w-6 flex items-center justify-center rounded text-gray-400 hover:bg-gray-100 hover:text-blue-600"
          >
            <ExternalLink className="w-3.5 h-3.5" aria-hidden />
          </a>
          <Button
            variant="ghost"
            size="icon-xs"
            onClick={onClose}
            aria-label="Close citation panel"
            title="Close citation panel"
            className="rounded text-gray-400"
          >
            <X className="w-4 h-4" aria-hidden />
          </Button>
        </div>
      </div>

      {/* Citation viewer (inline) */}
      <div className="flex-1 relative overflow-hidden">
        <CitationViewerInline
          key={citationStableKey(selectedCitation)}
          citation={selectedCitation}
        />
      </div>
    </div>
  );
};
